<template>
  <NoAccessEmpty v-if="!getHasAccess" />
  <div v-else v-loading="isLoading">
    <SSearchTable
      :config="config"
      :group-id="groupId"
      :cell-renderers="cellRenderers"
      @loading-state:changed="onLoadingChanged"
    />
  </div>

  <SDialog v-model:open="dialogs.ticketsOpen.value">
    <SDialogContent
      class="tickets-list-popup !w-[700px] !max-w-[700px]"
      @open-auto-focus.prevent
    >
      <SDialogHeader>
        <SDialogTitle>{{ $t("button.products") }}</SDialogTitle>
      </SDialogHeader>
      <div class="tickets-table-wrapper">
        <el-table :data="dialogs.ticketsList.value">
          <el-table-column prop="id" label="ID" width="auto" />
          <el-table-column
            prop="name"
            :label="$t('orders.name')"
            width="auto"
          />
          <el-table-column
            prop="productName"
            :label="$t('common.product-name')"
          />
        </el-table>
      </div>
    </SDialogContent>
  </SDialog>

  <SDialog v-model:open="dialogs.retailsOpen.value">
    <SDialogContent class="tickets-list-popup !w-[700px] !max-w-[700px]">
      <SDialogHeader>
        <SDialogTitle>{{ $t("orders.retail-product") }}</SDialogTitle>
      </SDialogHeader>
      <div class="tickets-table-wrapper">
        <el-table :data="dialogs.retailsList.value">
          <el-table-column
            prop="price_name"
            :label="$t('orders.name')"
            width="auto"
          />
          <el-table-column prop="quantity" :label="$t('orders.quantity')" />
        </el-table>
      </div>
    </SDialogContent>
  </SDialog>

  <SDialog v-model:open="dialogs.addonsOpen.value">
    <SDialogContent class="tickets-list-popup !w-[700px] !max-w-[700px]">
      <SDialogHeader>
        <SDialogTitle>{{ $t("orders.addons") }}</SDialogTitle>
      </SDialogHeader>
      <div class="tickets-table-wrapper">
        <el-table :data="dialogs.addonsList.value">
          <el-table-column
            prop="name"
            :label="$t('orders.name')"
            width="auto"
          />
          <el-table-column prop="quantity" :label="$t('orders.quantity')" />
        </el-table>
      </div>
    </SDialogContent>
  </SDialog>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { SSearchTable } from "@smeetz/ui-components";
import {
  SDialog,
  SDialogContent,
  SDialogHeader,
  SDialogTitle,
} from "@smeetz/ui-core";

import NoAccessEmpty from "@/components/common/NoAccessEmpty.vue";
import UserModule from "@/store/modules/user";

import { isCheckInEnabled } from "@/utils/helpers/feature-flags";

import { buildOrdersSearchTableConfig } from "./orders-search-table.config";
import { useOrdersAddonsProvide } from "./composables/useOrdersAddons";
import { useOrdersCheckInProvide } from "./composables/useOrdersCheckIn";
import { useOrdersDialogsProvide } from "./composables/useOrdersDialogs";

import OrderReferenceCell from "./custom-cells/OrderReferenceCell/OrderReferenceCell.vue";
import OrderVisitDateCell from "./custom-cells/OrderVisitDateCell/OrderVisitDateCell.vue";
import OrderTicketsCell from "./custom-cells/OrderTicketsCell/OrderTicketsCell.vue";
import OrderRetailsCell from "./custom-cells/OrderRetailsCell/OrderRetailsCell.vue";
import OrderAddonsCell from "./custom-cells/OrderAddonsCell/OrderAddonsCell.vue";
import OrderTeamsCell from "./custom-cells/OrderTeamsCell/OrderTeamsCell.vue";
import OrderEditActionCell from "./custom-cells/OrderEditActionCell/OrderEditActionCell.vue";
import OrderCheckInCell from "./custom-cells/OrderCheckInCell/OrderCheckInCell.vue";

const props = defineProps<{
  groupId: number;
  showTeamsColumn: boolean;
}>();

// The booking engine is granted to manager-level roles only. A seller gets a
// null token, which the table would render as an empty list.
const getHasAccess = computed(
  () => !!UserModule.getUser?.elasticSearchTokens?.booking
);

const config = computed(() =>
  buildOrdersSearchTableConfig({
    showTeamsColumn: props.showTeamsColumn,
    showCheckInColumn: isCheckInEnabled(),
  })
);

const cellRenderers = {
  reference: OrderReferenceCell,
  booking_first_start_date: OrderVisitDateCell,
  booking_tickets: OrderTicketsCell,
  bookings_ticket_quantity: OrderRetailsCell,
  addons: OrderAddonsCell,
  booking_team: OrderTeamsCell,
  check_in: OrderCheckInCell,
  actions: OrderEditActionCell,
};

// Shared per-row addons cache + queue (cells inject and request by booking id).
const addons = useOrdersAddonsProvide();

// Shared per-row check-in counts, batched the same way.
const checkInCounts = useOrdersCheckInProvide();

// Shared "show more" dialogs (cells inject and call openTickets/openRetails/openAddons).
const dialogs = useOrdersDialogsProvide();

const isLoading = ref(true);

/**
 * Mirror SSearchTable's loading state into the el-loading overlay and clear
 * the addons cache on each new fetch (pending/cached addons may no longer
 * match the row set about to land).
 *
 * @param {boolean} loading - True when fetch is in-flight, false on settle.
 * @returns {boolean} Sentinel.
 */
function onLoadingChanged(loading: boolean): boolean {
  isLoading.value = loading;

  if (loading) {
    addons.clear();
    checkInCounts.clear();
  }

  return true;
}
</script>
